import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';

import {
  Add,
  Download,
  Schedule,
  Assessment,
  ExpandMore,
  Refresh,
  Delete,
  Edit,
  Visibility,
  Email,
  FileDownload,
  PictureAsPdf,
  TableChart,
  BarChart,
  PieChart,
  Timeline,
  FilterList,
  Save,
  PlayArrow,
  Stop
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: 'system_overview' | 'customer_metrics' | 'alert_analytics' | 'check_in_trends' | 'caregiver_analytics' | 'custom';
  parameters: ReportParameter[];
  format: 'csv' | 'pdf';
  schedule?: ReportSchedule;
}

interface ReportParameter {
  name: string;
  type: 'date_range' | 'customer_id' | 'alert_type' | 'severity' | 'status' | 'text';
  label: string;
  required: boolean;
  defaultValue?: any;
  options?: string[];
}

interface ReportSchedule {
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string;
  recipients: string[];
  enabled: boolean;
}

interface GeneratedReport {
  id: string;
  name: string;
  type: string;
  format: string;
  generated_at: string;
  download_url?: string;
  status: 'pending' | 'completed' | 'failed';
}

const ReportGenerator: React.FC = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [generatedReports, setGeneratedReports] = useState<GeneratedReport[]>([]);
  const [scheduledReports, setScheduledReports] = useState<any[]>([]);
  const [reportHistory, setReportHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [scheduleFormData, setScheduleFormData] = useState<Record<string, any>>({
    frequency: 'weekly',
    time: '09:00',
    recipients: '',
    format: 'pdf'
  });

  // Predefined report templates
  const defaultTemplates: ReportTemplate[] = [
    {
      id: 'system_overview',
      name: 'System Overview Report',
      description: 'High-level system metrics and performance indicators',
      type: 'system_overview',
      parameters: [
        {
          name: 'date_range',
          type: 'date_range',
          label: 'Date Range',
          required: true,
          defaultValue: '30'
        }
      ],
      format: 'pdf'
    },
    {
      id: 'customer_metrics',
      name: 'Customer Analytics Report',
      description: 'Detailed customer metrics and health scores',
      type: 'customer_metrics',
      parameters: [
        {
          name: 'date_range',
          type: 'date_range',
          label: 'Date Range',
          required: true,
          defaultValue: '30'
        },
        {
          name: 'customer_id',
          type: 'customer_id',
          label: 'Specific Customer (Optional)',
          required: false
        }
      ],
      format: 'csv'
    },
    {
      id: 'alert_analytics',
      name: 'Alert Analytics Report',
      description: 'Comprehensive alert analysis and trends',
      type: 'alert_analytics',
      parameters: [
        {
          name: 'date_range',
          type: 'date_range',
          label: 'Date Range',
          required: true,
          defaultValue: '30'
        },
        {
          name: 'alert_type',
          type: 'alert_type',
          label: 'Alert Type',
          required: false,
          options: ['missed_checkin', 'negative_response', 'escalation']
        },
        {
          name: 'severity',
          type: 'severity',
          label: 'Severity Level',
          required: false,
          options: ['low', 'medium', 'high']
        }
      ],
      format: 'pdf'
    },
    {
      id: 'check_in_trends',
      name: 'Check-in Trends Report',
      description: 'Check-in patterns and response time analysis',
      type: 'check_in_trends',
      parameters: [
        {
          name: 'date_range',
          type: 'date_range',
          label: 'Date Range',
          required: true,
          defaultValue: '30'
        }
      ],
      format: 'csv'
    },
    {
      id: 'caregiver_analytics',
      name: 'Caregiver Analytics Report',
      description: 'Caregiver circle metrics and invitation statistics',
      type: 'caregiver_analytics',
      parameters: [],
      format: 'pdf'
    }
  ];

  useEffect(() => {
    setTemplates(defaultTemplates);
    fetchGeneratedReports();
    fetchScheduledReports();
    fetchReportHistory();
  }, []);

  const fetchGeneratedReports = async () => {
    try {
      // This would typically fetch from an API endpoint
      // For now, we'll use mock data
      const mockReports: GeneratedReport[] = [
        {
          id: '1',
          name: 'System Overview Report',
          type: 'system_overview',
          format: 'pdf',
          generated_at: new Date().toISOString(),
          status: 'completed'
        }
      ];
      setGeneratedReports(mockReports);
    } catch (err) {
      console.error('Error fetching generated reports:', err);
    }
  };

  const fetchScheduledReports = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch('/api/v1/scheduled-reports/scheduled', { headers });
      if (response.ok) {
        const data = await response.json();
        setScheduledReports(data);
      }
    } catch (err) {
      console.error('Error fetching scheduled reports:', err);
    }
  };

  const fetchReportHistory = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch('/api/v1/scheduled-reports/history', { headers });
      if (response.ok) {
        const data = await response.json();
        setReportHistory(data);
      }
    } catch (err) {
      console.error('Error fetching report history:', err);
    }
  };

  const handleGenerateReport = async (template: ReportTemplate) => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Determine the export endpoint based on template type and format
      let endpoint = '';
      const params = new URLSearchParams();

      // Add date range if specified
      if (formData.date_range) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - parseInt(formData.date_range));
        params.append('start_date', startDate.toISOString().split('T')[0]);
        params.append('end_date', endDate.toISOString().split('T')[0]);
      }

      // Add other parameters
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'date_range' && value) {
          params.append(key, value.toString());
        }
      });

      switch (template.type) {
        case 'system_overview':
          endpoint = `/api/v1/reports/export/system-overview/${template.format}`;
          break;
        case 'customer_metrics':
          endpoint = `/api/v1/reports/export/customer-metrics/${template.format}`;
          break;
        case 'alert_analytics':
          endpoint = `/api/v1/reports/export/alert-analytics/${template.format}`;
          break;
        case 'check_in_trends':
          endpoint = `/api/v1/reports/export/check-in-trends/${template.format}`;
          break;
        default:
          throw new Error('Unsupported report type');
      }

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }

      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }

      // Handle file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.${template.format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Add to generated reports list
      const newReport: GeneratedReport = {
        id: Date.now().toString(),
        name: template.name,
        type: template.type,
        format: template.format,
        generated_at: new Date().toISOString(),
        status: 'completed'
      };

      setGeneratedReports(prev => [newReport, ...prev]);
      setReportDialogOpen(false);
      setFormData({});

    } catch (err) {
      console.error('Error generating report:', err);
      setError('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleReport = async (template: ReportTemplate) => {
    try {
      setLoading(true);
      setError(null);

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const recipients = scheduleFormData.recipients.split(',').map((email: string) => email.trim()).filter((email: string) => email);

      const response = await fetch('/api/v1/scheduled-reports/scheduled', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: template.name,
          report_type: template.type,
          frequency: scheduleFormData.frequency,
          time: scheduleFormData.time,
          recipients: recipients,
          parameters: formData,
          format: scheduleFormData.format
        })
      });

      if (response.ok) {
        alert('Report scheduled successfully!');
        setScheduleDialogOpen(false);
        setScheduleFormData({
          frequency: 'weekly',
          time: '09:00',
          recipients: '',
          format: 'pdf'
        });
        fetchScheduledReports(); // Refresh the list
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to schedule report');
      }

    } catch (err) {
      console.error('Error scheduling report:', err);
      setError('Failed to schedule report');
    } finally {
      setLoading(false);
    }
  };

  const handleParameterChange = (parameterName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parameterName]: value
    }));
  };

  const renderParameterInput = (parameter: ReportParameter) => {
    switch (parameter.type) {
      case 'date_range':
        return (
          <FormControl fullWidth>
            <InputLabel>{parameter.label}</InputLabel>
            <Select
              value={formData[parameter.name] || parameter.defaultValue || ''}
              label={parameter.label}
              onChange={(e) => handleParameterChange(parameter.name, e.target.value)}
            >
              <MenuItem value="7">Last 7 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
              <MenuItem value="90">Last 90 days</MenuItem>
            </Select>
          </FormControl>
        );

      case 'customer_id':
        return (
          <TextField
            fullWidth
            label={parameter.label}
            value={formData[parameter.name] || ''}
            onChange={(e) => handleParameterChange(parameter.name, e.target.value)}
            placeholder="Enter customer ID (optional)"
          />
        );

      case 'alert_type':
      case 'severity':
        return (
          <FormControl fullWidth>
            <InputLabel>{parameter.label}</InputLabel>
            <Select
              value={formData[parameter.name] || ''}
              label={parameter.label}
              onChange={(e) => handleParameterChange(parameter.name, e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {parameter.options?.map(option => (
                <MenuItem key={option} value={option}>
                  {option.replace('_', ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return (
          <TextField
            fullWidth
            label={parameter.label}
            value={formData[parameter.name] || ''}
            onChange={(e) => handleParameterChange(parameter.name, e.target.value)}
            required={parameter.required}
          />
        );
    }
  };

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Report Generator
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Create custom reports and schedule automated exports
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Report Templates */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Report Templates
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            {templates.map((template) => (
              <Card key={template.id} variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {template.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={template.format.toUpperCase()}
                      color={template.format === 'pdf' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </Box>
                  
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setReportDialogOpen(true);
                      }}
                    >
                      Generate
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Schedule />}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setScheduleDialogOpen(true);
                      }}
                    >
                      Schedule
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Scheduled Reports */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scheduled Reports
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Next Run</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scheduledReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{report.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={report.report_type.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{report.frequency}</TableCell>
                    <TableCell>{report.time}</TableCell>
                    <TableCell>{report.recipients.join(', ')}</TableCell>
                    <TableCell>
                      {report.next_run ? new Date(report.next_run).toLocaleString() : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.enabled ? 'Active' : 'Disabled'}
                        color={report.enabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Run Now">
                          <IconButton size="small" color="primary">
                            <PlayArrow />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Report History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report History
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Report Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Generated</TableCell>
                  <TableCell>File Size</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportHistory.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{report.report_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={report.report_type.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.format.toUpperCase()}
                        color={report.format === 'pdf' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(report.generated_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {(report.file_size / 1024).toFixed(1)} KB
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status}
                        color={report.status === 'completed' ? 'success' : report.status === 'pending' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Download">
                          <IconButton 
                            size="small"
                            onClick={() => window.open(`/api/v1/scheduled-reports/history/${report.id}/download`, '_blank')}
                          >
                            <FileDownload />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Generate Report Dialog */}
      <Dialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Generate Report: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {selectedTemplate.description}
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                {selectedTemplate.parameters.map((parameter) => (
                  <Box key={parameter.name} sx={{ mb: 2 }}>
                    {renderParameterInput(parameter)}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => selectedTemplate && handleGenerateReport(selectedTemplate)}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Download />}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Report Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Schedule Report: {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Frequency</InputLabel>
              <Select 
                value={scheduleFormData.frequency}
                label="Frequency"
                onChange={(e) => setScheduleFormData(prev => ({ ...prev, frequency: e.target.value }))}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              type="time"
              label="Time"
              value={scheduleFormData.time}
              onChange={(e) => setScheduleFormData(prev => ({ ...prev, time: e.target.value }))}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Format</InputLabel>
              <Select 
                value={scheduleFormData.format}
                label="Format"
                onChange={(e) => setScheduleFormData(prev => ({ ...prev, format: e.target.value }))}
              >
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Email Recipients"
              placeholder="email1@example.com, email2@example.com"
              helperText="Separate multiple emails with commas"
              value={scheduleFormData.recipients}
              onChange={(e) => setScheduleFormData(prev => ({ ...prev, recipients: e.target.value }))}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => selectedTemplate && handleScheduleReport(selectedTemplate)}
            disabled={loading}
          >
            {loading ? 'Scheduling...' : 'Schedule Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportGenerator;
